import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  X,
  Smartphone,
  CalendarClock,
  BarChart3,
  Palette,
  Bell,
  Users,
  QrCode,
  Banknote,
  Sparkles,
  ChevronDown,
  Plus,
  Minus,
} from 'lucide-react';
import Logo from './Logo';
import HeroMock from './HeroMock';
import ConfettiField from './ConfettiField';
import { smallBurst, bigBurst } from './confettiBurst';

const features = [
  {
    icon: Smartphone,
    title: 'Tap-to-pay parking',
    body:
      'Apple Pay, Google Pay, cards — your customers pay in 8 seconds without making an account or downloading an app.',
    color: 'pink',
  },
  {
    icon: CalendarClock,
    title: 'Monthly subscriptions',
    body:
      'Capture the regulars. Auto-renewing monthlies, capacity limits, and start dates that actually make sense.',
    color: 'blue',
  },
  {
    icon: BarChart3,
    title: 'A dashboard you’ll open',
    body:
      'Live revenue, today vs. last month, active sessions, search any plate. From your phone or laptop, anywhere.',
    color: 'lime',
  },
  {
    icon: Bell,
    title: 'Renew from the couch',
    body:
      'Customers get a heads-up before their session ends and tap once to extend. The renewal you used to lose is now revenue.',
    color: 'yellow',
  },
  {
    icon: Palette,
    title: 'Brandable, not bland',
    body:
      'Your lot name, your colors, your URL. Customers see you — not us — when they pay.',
    color: 'coral',
  },
  {
    icon: Users,
    title: 'Bring your team',
    body:
      'Add managers, attendants, or your accountant with role-based access. Everyone sees what they should.',
    color: 'purple',
  },
] as const;

type AccentColor = (typeof features)[number]['color'];

const accentBg: Record<AccentColor, string> = {
  pink: 'bg-confetti-pink-soft',
  blue: 'bg-confetti-blue-soft',
  lime: 'bg-confetti-lime-soft',
  yellow: 'bg-confetti-yellow-soft',
  coral: 'bg-confetti-coral-soft',
  purple: 'bg-confetti-purple/15',
};
const accentText: Record<AccentColor, string> = {
  pink: 'text-confetti-pink',
  blue: 'text-confetti-blue',
  lime: 'text-confetti-lime',
  yellow: 'text-yellow-600',
  coral: 'text-confetti-coral',
  purple: 'text-confetti-purple',
};

const steps = [
  {
    n: '01',
    icon: Sparkles,
    title: 'Sign up free',
    body: 'Tell us about your lot, set your rates. Stripe Connect onboarding takes about 5 minutes.',
  },
  {
    n: '02',
    icon: QrCode,
    title: 'Put up the sign',
    body:
      'We send you a QR code with your lot name on it. Stick it on a post. That’s the entire installation.',
  },
  {
    n: '03',
    icon: Smartphone,
    title: 'People park',
    body:
      'They scan, type a plate, tap Apple Pay. They get a receipt. You get a notification. Confetti, technically.',
  },
  {
    n: '04',
    icon: Banknote,
    title: 'Money lands',
    body:
      'Direct deposits to your bank from Stripe. Watch revenue climb on the dashboard. We take 10%, you keep 90%.',
  },
] as const;

const comparison: Array<{
  label: string;
  meter: { yes: boolean; note: string };
  pc: { yes: boolean; note: string };
}> = [
  {
    label: 'Upfront cost',
    meter: { yes: false, note: '$5,000–$15,000 per machine' },
    pc: { yes: true, note: '$0. Free to start.' },
  },
  {
    label: 'Setup time',
    meter: { yes: false, note: 'Weeks of install + permits' },
    pc: { yes: true, note: 'Same-day. Print a sign.' },
  },
  {
    label: 'Paper / receipts',
    meter: { yes: false, note: 'Paper jams and refills forever' },
    pc: { yes: true, note: 'Digital receipts, automatic' },
  },
  {
    label: 'Monthly subscriptions',
    meter: { yes: false, note: 'Not really' },
    pc: { yes: true, note: 'Auto-renewing, capacity-aware' },
  },
  {
    label: 'Renew from phone',
    meter: { yes: false, note: 'Walk back to the machine' },
    pc: { yes: true, note: 'One tap before time runs out' },
  },
  {
    label: 'Maintenance',
    meter: { yes: false, note: 'Service calls, vandalism, weather' },
    pc: { yes: true, note: 'Software. We update it.' },
  },
  {
    label: 'You only pay if it works',
    meter: { yes: false, note: 'You pay either way' },
    pc: { yes: true, note: '10% of revenue. Nothing else.' },
  },
];

const faqs: Array<{ q: string; a: string }> = [
  {
    q: 'How does the 10% pricing actually work?',
    a:
      'When a customer pays for parking, Stripe deposits the full amount to your bank account. At the end of each month, we invoice 10% of what you collected. No setup fees, no monthly minimum, no surprises. If you make $0 in a month, you owe $0.',
  },
  {
    q: 'Where does the 30% revenue lift come from?',
    a:
      'Two places, mostly. First: monthly subscriptions. Almost no meter offers them, so you’ve been leaving regulars on the table. Second: renewals. Customers who would have left when their hour ran out now extend from their phone, often more than once. The rest is downstream — fewer disputes, no broken machines, no missed payments.',
  },
  {
    q: 'Do my customers need to download an app?',
    a:
      'No. They scan a QR code, your lot’s page opens in their browser, they type a license plate, and they tap Apple Pay or Google Pay. No account, no app, no friction. We tested and the median time from scan to paid is under 15 seconds.',
  },
  {
    q: 'How do I get the money?',
    a:
      'Stripe Connect. The funds go straight from your customer’s card to your bank account on Stripe’s normal payout schedule (usually 2 business days). We never touch your money — we only invoice our 10% separately.',
  },
  {
    q: 'What if my customers don’t pay?',
    a:
      'You enforce however you do today: tickets, towing, attendant. ParkConfetti gives you a license-plate lookup so you can confirm in two seconds whether any plate in your lot is paid. Most owners we talk to find this faster than what they had with meters.',
  },
  {
    q: 'Can I keep my current pricing structure?',
    a:
      'Yes. Set any hourly rate, daily cap, max hours, and monthly subscription price you want. Change it anytime from your dashboard. You can also cap the number of monthly subs to protect your daily turnover.',
  },
];

export default function MarketingHome() {
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const heroBurstedRef = useRef(false);
  const finalCtaRef = useRef<HTMLDivElement | null>(null);

  // Stat strip count-ups
  const statStripRef = useRef<HTMLDivElement | null>(null);
  const [statsAnimated, setStatsAnimated] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Tiny celebratory burst once when hero is in view (not on every scroll)
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (heroBurstedRef.current) return;
      heroBurstedRef.current = true;
      smallBurst(0.5, 0.25);
    }, 600);
    return () => window.clearTimeout(t);
  }, []);

  // Trigger a bigger burst when the final CTA scrolls into view
  useEffect(() => {
    if (!finalCtaRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            bigBurst();
            obs.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    obs.observe(finalCtaRef.current);
    return () => obs.disconnect();
  }, []);

  // Stat strip count-up trigger
  useEffect(() => {
    if (!statStripRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !statsAnimated) {
            setStatsAnimated(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(statStripRef.current);
    return () => obs.disconnect();
  }, [statsAnimated]);

  return (
    <div className="min-h-screen bg-cream-100 text-ink-900 selection:bg-confetti-pink selection:text-white">
      {/* ──────────────────────────── NAV ──────────────────────────── */}
      <nav
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-cream-100/85 backdrop-blur-md border-b border-ink-300/40'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="hover:opacity-90 transition-opacity">
            <Logo size={32} />
          </Link>

          <div className="hidden md:flex items-center gap-7 text-sm font-medium text-ink-700">
            <a href="#how-it-works" className="hover:text-ink-900 transition-colors">
              How it works
            </a>
            <a href="#features" className="hover:text-ink-900 transition-colors">
              Features
            </a>
            <a href="#pricing" className="hover:text-ink-900 transition-colors">
              Pricing
            </a>
            <a href="#faq" className="hover:text-ink-900 transition-colors">
              FAQ
            </a>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/admin"
              className="hidden sm:inline-flex text-sm font-semibold text-ink-700 hover:text-ink-900 px-3 py-2 transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/signup/lot-owner"
              onMouseEnter={() => smallBurst(0.85, 0.08)}
              className="group inline-flex items-center gap-1.5 bg-ink-900 hover:bg-ink-800 text-cream-50 text-sm font-semibold px-4 py-2.5 rounded-full transition-all hover:scale-[1.03]"
            >
              Get started free
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </nav>

      {/* ──────────────────────────── HERO ─────────────────────────── */}
      <section className="relative pt-32 sm:pt-36 pb-20 px-5 sm:px-6 overflow-hidden">
        {/* Background flourishes */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-confetti-pink/20 rounded-full blur-3xl" />
          <div className="absolute top-40 -right-32 w-[420px] h-[420px] bg-confetti-blue/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-[360px] h-[360px] bg-confetti-yellow/20 rounded-full blur-3xl" />
        </div>
        <ConfettiField density={36} />

        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-white border border-ink-300/50 rounded-full pl-1.5 pr-4 py-1.5 text-xs font-medium text-ink-700 mb-6 shadow-soft">
              <span className="bg-confetti-pink text-white text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5">
                New
              </span>
              For small lot owners who are tired of meters
            </div>

            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-balance">
              Your parking lot
              <br className="hidden sm:block" />{' '}
              just got a{' '}
              <span className="relative inline-block">
                <span className="relative z-10">30% raise.</span>
                <span className="absolute bottom-2 left-0 right-0 h-3 bg-confetti-yellow/70 -z-0 -rotate-1" />
              </span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-ink-700 max-w-xl text-balance leading-relaxed">
              ParkConfetti turns any lot into a tap-to-pay party. Customers pay from
              their phone, renew their session from the couch, and you watch revenue
              land in your bank — no meters, no paper, no monthly fees.{' '}
              <span className="font-semibold text-ink-900">
                We take 10%, so we only celebrate when you do.
              </span>
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/signup/lot-owner"
                onMouseEnter={() => smallBurst(0.5, 0.6)}
                className="group inline-flex items-center justify-center gap-2 bg-confetti-pink hover:bg-confetti-pink/90 text-white font-semibold px-7 py-4 rounded-full shadow-confetti transition-all hover:scale-[1.03]"
              >
                Start the party — it’s free
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 text-ink-900 font-semibold px-6 py-4 rounded-full border border-ink-300 hover:border-ink-700 transition-colors"
              >
                See how it works
                <ChevronDown className="w-4 h-4" />
              </a>
            </div>

            <div className="mt-8 flex items-center gap-4 text-sm text-ink-500">
              <div className="flex -space-x-2">
                {['#FF3D7F', '#3D7FFF', '#A8E060', '#FFD93D'].map((c) => (
                  <div
                    key={c}
                    className="w-7 h-7 rounded-full border-2 border-cream-100"
                    style={{ background: c }}
                  />
                ))}
              </div>
              <span>
                Trusted by independent lot owners across Ottawa.{' '}
                <span className="text-ink-700 font-medium">No credit card to start.</span>
              </span>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <HeroMock />
          </div>
        </div>
      </section>

      {/* ────────────────────── STAT STRIP ────────────────────── */}
      <section
        ref={statStripRef}
        className="relative py-14 px-5 sm:px-6 bg-ink-900 text-cream-50"
      >
        <ConfettiField density={20} className="opacity-40" />
        <div className="relative max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-6 text-center">
          <Stat label="more revenue, on average" value="30%" suffix="" highlight />
          <Stat label="of your revenue. that’s our cut." value="10%" suffix="" />
          <Stat label="from sign-up to first payment" value="5" suffix=" min" />
        </div>
      </section>

      {/* ───────────────────── WHERE 30% COMES FROM ────────────────── */}
      <section className="relative py-24 px-5 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <SectionHeader
            kicker="Where the 30% comes from"
            title="It’s not magic. It’s two boring things."
            sub="The lift is real, repeatable, and explainable. Here’s exactly where the extra money is hiding in your lot today."
          />

          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <MechanismCard
              accent="pink"
              eyebrow="Mechanism #1"
              title="Monthly subscriptions you couldn’t offer before"
              body="Your meter can’t do this. ParkConfetti can. Regulars stop paying you $5 a day and start paying you $150 a month — automatically, every month, while they sleep. Most lots fill 10–25 monthly slots within the first 90 days."
              kpi="+$1,500/mo"
              kpiLabel="from 10 average monthlies"
            />
            <MechanismCard
              accent="blue"
              eyebrow="Mechanism #2"
              title="Renewals you used to lose"
              body="Customer paid for an hour, dinner ran long, walked back to a $60 ticket — and you got nothing. Now they get a text 15 minutes before expiry, tap once, and pay you for another hour without leaving the table."
              kpi="+18%"
              kpiLabel="extension capture rate"
            />
          </div>

          <div className="mt-8 bg-white rounded-2xl border border-ink-300/50 p-6 sm:p-8 shadow-soft flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-14 h-14 rounded-2xl bg-confetti-yellow flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-7 h-7 text-ink-900" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-ink-500 mb-1">
                Bonus: the friction tax goes away
              </div>
              <p className="text-ink-700">
                No more “I didn’t have change.” No more paper jam at 7pm on a Saturday.
                No more disputed receipts. Drivers who would have skipped paying because
                the machine was annoying… now just pay.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────── HOW IT WORKS ─────────────────────── */}
      <section id="how-it-works" className="relative py-24 px-5 sm:px-6 bg-cream-200/60">
        <div className="max-w-6xl mx-auto">
          <SectionHeader
            kicker="How it works"
            title="Live by lunch."
            sub="Most lot owners go from sign-up to taking their first real payment on the same day. The hardest step is taping a sign to a post."
          />

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 relative">
            {steps.map((step) => (
              <div
                key={step.n}
                className="relative bg-white rounded-3xl border border-ink-300/40 p-6 shadow-soft"
              >
                <div className="absolute -top-4 left-6 bg-ink-900 text-cream-50 text-xs font-display font-bold rounded-full px-3 py-1">
                  {step.n}
                </div>
                <div className="w-12 h-12 rounded-2xl bg-confetti-pink-soft flex items-center justify-center mb-4">
                  <step.icon className="w-6 h-6 text-confetti-pink" />
                </div>
                <h3 className="font-display font-bold text-xl text-ink-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-ink-700 text-sm leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────── FEATURES ────────────────────────── */}
      <section id="features" className="relative py-24 px-5 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <SectionHeader
            kicker="Features"
            title="Built for the way small lots actually run."
            sub="Not a watered-down version of enterprise parking software. Designed from day one for one to three lots, run by people who have other things to do."
          />

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => (
              <div
                key={f.title}
                className="group bg-white rounded-2xl border border-ink-300/40 p-6 hover:border-ink-700/30 hover:shadow-lift transition-all"
              >
                <div
                  className={`w-12 h-12 rounded-xl ${accentBg[f.color]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                >
                  <f.icon className={`w-6 h-6 ${accentText[f.color]}`} />
                </div>
                <h3 className="font-display font-bold text-lg text-ink-900 mb-2">
                  {f.title}
                </h3>
                <p className="text-ink-700 text-sm leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────── METER vs PARKCONFETTI ─────────────── */}
      <section className="relative py-24 px-5 sm:px-6 bg-cream-200/60">
        <div className="max-w-5xl mx-auto">
          <SectionHeader
            kicker="The old way vs. the new way"
            title="Your meter, on the left. Us, on the right."
            sub="If you currently run a pay-station, this is what you’re actually paying for. If you don’t — well, this is what you’re missing."
          />

          <div className="mt-12 bg-white rounded-3xl border border-ink-300/40 overflow-hidden shadow-soft">
            <div className="grid grid-cols-3 bg-ink-900 text-cream-50">
              <div className="p-5 sm:p-6 text-sm font-semibold uppercase tracking-wider opacity-70">
                What you’re comparing
              </div>
              <div className="p-5 sm:p-6 text-sm font-semibold uppercase tracking-wider opacity-70 border-l border-ink-700">
                Pay-station / meter
              </div>
              <div className="p-5 sm:p-6 text-sm font-semibold uppercase tracking-wider border-l border-ink-700 bg-gradient-to-r from-confetti-pink to-confetti-coral text-white">
                ParkConfetti
              </div>
            </div>
            {comparison.map((row, i) => (
              <div
                key={row.label}
                className={`grid grid-cols-3 ${
                  i % 2 === 0 ? 'bg-white' : 'bg-cream-100'
                } border-t border-ink-300/40`}
              >
                <div className="p-4 sm:p-5 font-semibold text-ink-900 text-sm sm:text-base">
                  {row.label}
                </div>
                <div className="p-4 sm:p-5 border-l border-ink-300/40 flex items-start gap-2">
                  <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-ink-300/50 flex-shrink-0">
                    <X className="w-3 h-3 text-ink-700" strokeWidth={3} />
                  </span>
                  <span className="text-sm text-ink-700">{row.meter.note}</span>
                </div>
                <div className="p-4 sm:p-5 border-l border-ink-300/40 flex items-start gap-2 bg-confetti-pink-soft/40">
                  <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-confetti-pink flex-shrink-0">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </span>
                  <span className="text-sm text-ink-900 font-medium">{row.pc.note}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────── PRICING ──────────────────────── */}
      <section id="pricing" className="relative py-24 px-5 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            kicker="Pricing"
            title="One number. Aligned with you."
            sub="No setup fees. No monthly minimum. No per-transaction fee. We make money the same way you do — when somebody parks."
          />

          <div className="mt-12 relative">
            {/* Tilted accent card behind */}
            <div className="absolute inset-0 bg-confetti-pink/15 rounded-[2rem] -rotate-1 -z-10" />

            <div className="relative bg-white rounded-[2rem] border border-ink-300/50 p-8 sm:p-12 shadow-lift">
              <div className="grid lg:grid-cols-5 gap-10 items-center">
                <div className="lg:col-span-2">
                  <div className="inline-flex items-center gap-2 bg-confetti-lime-soft text-ink-900 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider mb-5">
                    <span className="w-1.5 h-1.5 rounded-full bg-confetti-lime" />
                    Pay-as-you-earn
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display font-black text-7xl sm:text-8xl text-ink-900 leading-none">
                      10
                    </span>
                    <span className="font-display font-bold text-4xl text-confetti-pink leading-none">
                      %
                    </span>
                  </div>
                  <p className="mt-3 text-ink-700">
                    of your monthly parking revenue. That’s the entire bill.
                  </p>
                  <p className="mt-2 text-sm text-ink-500">
                    Customers see ~30% more revenue, so you net 20%. We only get paid
                    when you do.
                  </p>
                </div>

                <div className="lg:col-span-3">
                  <div className="text-sm font-semibold uppercase tracking-wider text-ink-500 mb-4">
                    Everything is included
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
                    {[
                      'Free to sign up & launch',
                      'Unlimited parking sessions',
                      'Unlimited team members',
                      'Real-time analytics dashboard',
                      'Hourly + monthly payment types',
                      'Apple Pay, Google Pay, cards',
                      'SMS expiry reminders',
                      'Stripe direct deposits',
                      'Custom branding & lot URL',
                      'Email support, fast',
                    ].map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2.5 text-sm text-ink-700"
                      >
                        <Check className="w-4 h-4 text-confetti-pink flex-shrink-0 mt-0.5" strokeWidth={3} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-ink-300/40 flex flex-col sm:flex-row items-center justify-between gap-5">
                <p className="text-sm text-ink-700 text-center sm:text-left">
                  <span className="font-semibold text-ink-900">No risk.</span> No
                  contract. Cancel anytime. If you don’t make money, neither do we.
                </p>
                <Link
                  to="/signup/lot-owner"
                  onMouseEnter={() => smallBurst(0.85, 0.85)}
                  className="group inline-flex items-center gap-2 bg-confetti-pink hover:bg-confetti-pink/90 text-white font-semibold px-7 py-3.5 rounded-full shadow-confetti transition-all hover:scale-[1.03]"
                >
                  Get started free
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────── FAQ ─────────────────────────── */}
      <section id="faq" className="relative py-24 px-5 sm:px-6 bg-cream-200/60">
        <div className="max-w-3xl mx-auto">
          <SectionHeader
            kicker="FAQ"
            title="Real questions from real lot owners."
            sub="Everything you wanted to ask before you click sign-up."
          />

          <div className="mt-12 space-y-3">
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <button
                  key={f.q}
                  onClick={() => setOpenFaq(open ? null : i)}
                  className={`w-full text-left bg-white rounded-2xl border transition-all ${
                    open
                      ? 'border-ink-700/40 shadow-lift'
                      : 'border-ink-300/40 hover:border-ink-700/30'
                  }`}
                >
                  <div className="flex items-center justify-between gap-4 p-5 sm:p-6">
                    <span className="font-display font-bold text-base sm:text-lg text-ink-900">
                      {f.q}
                    </span>
                    <span
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        open ? 'bg-confetti-pink text-white' : 'bg-cream-200 text-ink-700'
                      }`}
                    >
                      {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </span>
                  </div>
                  {open && (
                    <div className="px-5 sm:px-6 pb-6 text-ink-700 leading-relaxed">
                      {f.a}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────────────────── FINAL CTA ───────────────────────── */}
      <section className="relative py-24 px-5 sm:px-6" ref={finalCtaRef}>
        <div className="max-w-5xl mx-auto relative">
          <ConfettiField density={30} />
          <div className="relative bg-ink-900 text-cream-50 rounded-[2.5rem] px-8 sm:px-16 py-16 sm:py-20 text-center overflow-hidden">
            {/* big floating shapes */}
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-confetti-pink/30 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-confetti-blue/30 rounded-full blur-3xl" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-cream-50/10 border border-cream-50/20 rounded-full px-4 py-1.5 text-xs font-medium mb-6">
                <Sparkles className="w-3.5 h-3.5 text-confetti-yellow" />
                Free to start. No credit card required.
              </div>
              <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance">
                Let’s throw your lot a{' '}
                <span className="text-confetti-pink">going-away party</span>{' '}
                for the meter.
              </h2>
              <p className="mt-5 text-lg text-cream-50/70 max-w-xl mx-auto">
                Sign up in under 5 minutes. Take your first real payment today. Watch
                your dashboard fill with confetti every time someone parks.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/signup/lot-owner"
                  onMouseEnter={() => smallBurst(0.5, 0.5)}
                  className="group inline-flex items-center justify-center gap-2 bg-confetti-pink hover:bg-confetti-pink/90 text-white font-semibold px-8 py-4 rounded-full shadow-confetti transition-all hover:scale-[1.03]"
                >
                  Start the party
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/lots"
                  className="inline-flex items-center justify-center gap-2 text-cream-50 font-semibold px-6 py-4 rounded-full border border-cream-50/30 hover:border-cream-50/60 transition-colors"
                >
                  I’m here to park, actually
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────── FOOTER ─────────────────────────── */}
      <footer className="relative py-12 px-5 sm:px-6 border-t border-ink-300/40">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <Logo size={28} />
          <div className="flex items-center gap-5 sm:gap-7 text-sm text-ink-500">
            <a href="#how-it-works" className="hover:text-ink-900 transition-colors">
              How it works
            </a>
            <a href="#features" className="hover:text-ink-900 transition-colors">
              Features
            </a>
            <a href="#pricing" className="hover:text-ink-900 transition-colors">
              Pricing
            </a>
            <Link to="/lots" className="hover:text-ink-900 transition-colors">
              Find parking
            </Link>
            <Link to="/admin" className="hover:text-ink-900 transition-colors">
              Sign in
            </Link>
          </div>
          <p className="text-sm text-ink-500 text-center md:text-right">
            © {new Date().getFullYear()} ParkConfetti.
            <br className="md:hidden" /> Built with confetti in Ottawa.
          </p>
        </div>
      </footer>
    </div>
  );
}

/* ───────────────── Internal sub-components ───────────────── */

function SectionHeader({
  kicker,
  title,
  sub,
}: {
  kicker: string;
  title: string;
  sub: string;
}) {
  return (
    <div className="text-center max-w-2xl mx-auto">
      <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-confetti-pink mb-4">
        <span className="w-6 h-px bg-confetti-pink" />
        {kicker}
        <span className="w-6 h-px bg-confetti-pink" />
      </div>
      <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-ink-900 leading-[1.1] text-balance">
        {title}
      </h2>
      <p className="mt-4 text-ink-700 text-lg leading-relaxed">{sub}</p>
    </div>
  );
}

function Stat({
  value,
  suffix,
  label,
  highlight,
}: {
  value: string;
  suffix: string;
  label: string;
  highlight?: boolean;
}) {
  return (
    <div>
      <div
        className={`font-display font-black text-5xl sm:text-6xl leading-none ${
          highlight ? 'text-confetti-pink' : 'text-cream-50'
        }`}
      >
        {value}
        <span className="text-cream-50/70 text-3xl">{suffix}</span>
      </div>
      <div className="mt-3 text-sm text-cream-50/70 max-w-[16ch] mx-auto">{label}</div>
    </div>
  );
}

function MechanismCard({
  accent,
  eyebrow,
  title,
  body,
  kpi,
  kpiLabel,
}: {
  accent: AccentColor;
  eyebrow: string;
  title: string;
  body: string;
  kpi: string;
  kpiLabel: string;
}) {
  return (
    <div className="bg-white rounded-3xl border border-ink-300/40 p-7 shadow-soft flex flex-col gap-5">
      <div
        className={`inline-flex items-center gap-2 ${accentBg[accent]} ${accentText[accent]} self-start rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider`}
      >
        {eyebrow}
      </div>
      <h3 className="font-display font-bold text-2xl text-ink-900 leading-tight text-balance">
        {title}
      </h3>
      <p className="text-ink-700 leading-relaxed">{body}</p>
      <div className="mt-auto pt-5 border-t border-ink-300/40 flex items-baseline gap-3">
        <span className={`font-display font-bold text-3xl ${accentText[accent]}`}>
          {kpi}
        </span>
        <span className="text-sm text-ink-500">{kpiLabel}</span>
      </div>
    </div>
  );
}
